/**
 * @module BaseProductSearchGrid
 * @description Base Alpine.js component factory for Algolia product search grids
 *
 * This factory creates reusable Algolia search grid components for product listings.
 * Used specifically for product search grids (Robots, CNC, Robodrill, etc.), NOT for
 * general site-wide search functionality.
 *
 * @param {Object} config - Configuration object
 * @param {string} config.appId - Algolia application ID
 * @param {string} config.apiKey - Algolia API key
 * @param {string} config.indexName - Algolia index name
 * @param {Function} config.configureWidget - Optional configure widget function
 * @param {Function} config.formatCardData - Function to format result data for card display
 * @param {Function} config.getSpecs - Function to get specs array from result
 * @param {Function} config.hasSpecs - Function to check if result has specs
 * @param {string} config.scrollToSelector - CSS selector for scroll target on pagination
 *
 * @returns {Function} Alpine.js component function
 *
 * @example
 * export default createProductSearchGrid({
 *   appId: robotsConfig.app_id,
 *   apiKey: robotsConfig.api_key,
 *   indexName: robotsConfig.index,
 *   configureWidget: configure,
 *   formatCardData: (result) => ({ ... }),
 *   getSpecs: (result) => [...],
 *   hasSpecs: (result) => boolean,
 *   scrollToSelector: '[data-search-robots]',
 * });
 */

import { algoliasearch } from 'algoliasearch';
import instantsearch from 'instantsearch.js';
import { createWidgetInstances } from '@js/algolia/createWidgetInstance.js';
import { setTexts, texts } from '@js/algolia/common.js';
import { connectHits } from 'instantsearch.js/es/connectors';
import {
  createPagination,
  createPaginationVisibility,
} from '@js/algolia/createPagination.js';
import {
  createCurrentRefinements,
  createLegacyToggleIndicator,
} from '@js/algolia/currentRefinements.js';
import {
  createStateMapping,
  historyRouter,
} from '@js/algolia/historyRouter.js';

export function createProductSearchGrid({
  appId,
  apiKey,
  indexName,
  configureWidget = null,
  formatCardData,
  getSpecs = null,
  hasSpecs = null,
  scrollToSelector = '[data-search-products]',
}) {
  return ($el) => {
    const component = {
      results: [],
      appId,
      apiKey,
      indexName,
      isLoaded: false,
      isSearchComplete: false,
      noResults: false,
      totalHits: 0,
      el: $el,
      search: null,
      activeRefinements: {},
      hasError: false,
      errorMessage: '',
      filtersOpen: false,
      _desktopQuery: null,
      _desktopQueryHandler: null,
      // Translation labels for product card attributes
      translations: {
        maxPayload: null,
        reach: null,
        maxControlledPaths: null,
        controlledAxesPath: null,
        spindleAxesPath: null,
        tableSize: null,
        xTravel: null,
        maxLoad: null,
        maxWorkpieceWeight: null,
        maxInjectionSpeed: null,
      },

      /**
       * Initializes the search setup
       */
      init() {
        setTexts(this.el);
        // Extract translation data attributes
        this.translations.maxPayload =
          this.el.dataset.textMaxPayload || 'Max Payload';
        this.translations.reach = this.el.dataset.textReach || 'Reach';
        this.translations.maxControlledPaths =
          this.el.dataset.textMaxControlledPaths || 'Max Controlled Paths';
        this.translations.controlledAxesPath =
          this.el.dataset.textControlledAxesPath || 'Controlled Axes / Path';
        this.translations.spindleAxesPath =
          this.el.dataset.textSpindleAxesPath || 'Spindle Axes / Path';
        this.translations.tableSize =
          this.el.dataset.textTableSize || 'Table Size';
        this.translations.xTravel = this.el.dataset.textXTravel || 'X Travel';
        this.translations.maxLoad = this.el.dataset.textMaxLoad || 'Max Load';
        this.translations.maxWorkpieceWeight =
          this.el.dataset.textMaxWorkpieceWeight || 'Max Workpiece Weight';
        this.translations.maxInjectionSpeed =
          this.el.dataset.textMaxInjectionSpeed || 'Max. Injection Speed';

        if (new URLSearchParams(window.location.search).has('fallback')) {
          this.handleError(texts.errorUnavailable);
          return;
        }
        if (!this.appId || !this.apiKey || !this.indexName) {
          this.handleError(texts.errorConfig);
          return;
        }
        this.setupSearch();
        this.watchDesktopBreakpoint();
      },

      /**
       * Open the mobile filters drawer
       */
      openFilters() {
        this.filtersOpen = true;
      },

      /**
       * Close the mobile filters drawer
       */
      closeFilters() {
        this.filtersOpen = false;
      },

      /**
       * Count of active refinements across all attributes. Drives the
       * badge on the mobile Filters trigger.
       * @returns {number}
       */
      get totalActiveRefinements() {
        return Object.values(this.activeRefinements).reduce(
          (sum, values) => sum + (Array.isArray(values) ? values.length : 0),
          0,
        );
      },

      /**
       * Close the drawer when the viewport crosses into desktop so stale
       * scroll-lock and focus-trap don't leak into the inline sidebar.
       */
      watchDesktopBreakpoint() {
        this._desktopQuery = window.matchMedia('(min-width: 1024px)');
        this._desktopQueryHandler = (e) => {
          if (e.matches && this.filtersOpen) {
            this.filtersOpen = false;
          }
        };
        this._desktopQuery.addEventListener(
          'change',
          this._desktopQueryHandler,
        );
      },

      /**
       * Alpine destroy hook — unbind the media query listener.
       */
      destroy() {
        if (this._desktopQuery && this._desktopQueryHandler) {
          this._desktopQuery.removeEventListener(
            'change',
            this._desktopQueryHandler,
          );
        }
      },

      /**
       * Handles errors by setting state and dispatching event
       * @param {string} message - Error message to display
       * @param {Error} [error] - Optional error object for logging
       */
      handleError(message, error = null) {
        this.hasError = true;
        this.errorMessage = message;
        // Intentionally do NOT flip isLoaded here — keep the skeleton grid
        // painted so the layout doesn't collapse while the error banner
        // renders above it.

        if (error) {
          console.error('Algolia search error:', error);
        }

        // Dispatch event for page-level handling
        window.dispatchEvent(
          new CustomEvent('algolia:error', {
            detail: { message, error, element: this.el },
          }),
        );
      },

      /**
       * Builds the initial UI state from toggle widgets with data-checked="true"
       * @returns {Object} The toggle state object
       */
      getInitialToggleState() {
        const toggleState = {};
        const toggleWidgets = document.querySelectorAll(
          '[data-widget-type="toggleRefinement"][data-checked="true"]',
        );
        toggleWidgets.forEach((widget) => {
          const attribute = widget.dataset.attribute;
          if (attribute) {
            toggleState[attribute] = true;
          }
        });
        return toggleState;
      },

      /**
       * Builds the initial refinement state from widgets with data-default-refinement
       * @returns {Object} The refinement list state object
       */
      getInitialRefinementState() {
        const refinementState = {};
        const widgets = document.querySelectorAll(
          '[data-widget-auto][data-default-refinement]',
        );
        widgets.forEach((widget) => {
          const attribute = widget.dataset.attribute;
          const defaultValue = widget.dataset.defaultRefinement;
          if (attribute && defaultValue) {
            // Support comma-separated values for multiple defaults
            refinementState[attribute] = defaultValue
              .split(',')
              .map((v) => v.trim());
          }
        });
        return refinementState;
      },

      /**
       * Sets up the Algolia search instance with necessary configurations and widgets
       * @async
       */
      async setupSearch() {
        try {
          const searchClient = this.createSearchClient();
          const toggleState = this.getInitialToggleState();
          const refinementState = this.getInitialRefinementState();

          this.search = instantsearch({
            indexName: this.indexName,
            searchClient,
            future: {
              preserveSharedStateOnUnmount: true,
              cleanUrlOnDispose: false,
            },
            initialUiState: {
              [this.indexName]: {
                toggle: toggleState,
                refinementList: refinementState,
              },
            },
            routing: {
              router: historyRouter(),
              stateMapping: createStateMapping(
                this.indexName,
                toggleState,
                refinementState,
              ),
            },
          });

          // Get all auto-configured widgets from the DOM
          const widgetContainers = Array.from(
            document.querySelectorAll('[data-widget-auto]'),
          );
          const widgetInstances = createWidgetInstances(widgetContainers);

          // Create the hits connector to pipe results to Alpine
          const structuredResults = this.generateStructuredResults();

          // Create pagination widget and visibility controller
          const paginationWidget = createPagination(
            '[data-pagination]',
            scrollToSelector,
          );
          const paginationVisibility =
            createPaginationVisibility('[data-pagination]');

          // Create current refinements widget
          const refinementsWidget = createCurrentRefinements();

          // Create legacy toggle indicator (shows chip when legacy products visible)
          const legacyIndicator = createLegacyToggleIndicator();

          // Build widgets array
          const widgets = [];

          // Add configure widget if provided
          if (configureWidget) {
            widgets.push(configureWidget);
          }

          widgets.push(...widgetInstances);
          widgets.push(structuredResults());

          if (paginationWidget) {
            widgets.push(paginationWidget);
          }

          if (paginationVisibility) {
            widgets.push(paginationVisibility);
          }

          if (refinementsWidget) {
            widgets.push(refinementsWidget);
          }

          if (legacyIndicator) {
            widgets.push(legacyIndicator);
          }

          this.search.addWidgets(widgets.filter(Boolean));

          // Listen for errors during search
          this.search.on('error', ({ error }) => this.onSearchError(error));
          this.search.on('render', () => this.onSearchRender());

          await this.search.start();
        } catch (error) {
          this.handleError(texts.errorUnavailable, error);
        }
      },

      /**
       * Creates a search client with error handling wrapper
       * @returns {Object} Algolia search client with error handling
       */
      createSearchClient() {
        const client = algoliasearch(this.appId, this.apiKey);
        const self = this;

        // Wrap the search method to catch API errors
        return {
          ...client,
          async search(requests) {
            try {
              return await client.search(requests);
            } catch (error) {
              self.onSearchError(error);
              throw error;
            }
          },
        };
      },

      /**
       * Handles search errors (API errors, rate limits, network issues)
       * @param {Error} error - The error object
       */
      onSearchError(error) {
        let message = texts.errorUnavailable;

        if (error.status === 429) {
          message = texts.errorRateLimit;
        } else if (error.status === 403 || error.status === 401) {
          message = texts.errorAuth;
        } else if (
          error.message?.includes('network') ||
          error.name === 'TypeError'
        ) {
          message = texts.errorNetwork;
        }

        this.handleError(message, error);
      },

      /**
       * Generates structured results using Algolia's connectHits connector
       * @returns {Function} The connectHits widget
       */
      generateStructuredResults() {
        return connectHits(({ hits, results }) => {
          // Only mark search complete when we have actual results metadata
          if (results?.nbHits !== undefined) {
            this.isSearchComplete = true;
            this.noResults = hits.length === 0;
          }
          this.results = hits;
          this.totalHits = results?.nbHits || 0;
        });
      },

      /**
       * Handles actions to perform after the search is rendered
       */
      onSearchRender() {
        this.isLoaded = true;
        this.updateActiveRefinements();
      },

      /**
       * Updates the active refinements from InstantSearch state
       */
      updateActiveRefinements() {
        if (!this.search) return;
        const uiState = this.search.getUiState();
        const indexState = uiState[this.indexName] || {};
        this.activeRefinements = indexState.refinementList || {};
      },

      /**
       * Check if multiple values are selected for a given attribute
       * @param {string} attribute - The attribute name to check
       * @returns {boolean} True if 2 or more values are selected
       */
      hasMultipleRefinements(attribute) {
        const values = this.activeRefinements[attribute];
        return values && values.length >= 2;
      },

      /**
       * Check if no filters are applied for a given attribute
       * @param {string} attribute - The attribute name to check
       * @returns {boolean} True if no values are selected
       */
      hasNoRefinements(attribute) {
        const values = this.activeRefinements[attribute];
        return !values || values.length === 0;
      },

      /**
       * Format array values for display
       * @param {Array} arr - Array of values
       * @param {string} separator - Separator string
       * @returns {string} Formatted string
       */
      formatArray(arr, separator = ', ') {
        if (!arr || !Array.isArray(arr)) return '';
        return arr.join(separator);
      },

      // Product-specific functions (passed in via config)
      getSpecs: getSpecs || (() => []),
      hasSpecs: hasSpecs || (() => false),
    };

    // Assigned after construction so the wrapper reads the component's own
    // translations; an in-object arrow would capture the wrong `this`.
    component.formatCardData = (result) =>
      formatCardData(result, {
        maxPayload: component.translations?.maxPayload,
        reach: component.translations?.reach,
        maxControlledPaths: component.translations?.maxControlledPaths,
        controlledAxesPath: component.translations?.controlledAxesPath,
        spindleAxesPath: component.translations?.spindleAxesPath,
        tableSize: component.translations?.tableSize,
        xTravel: component.translations?.xTravel,
        maxLoad: component.translations?.maxLoad,
        maxWorkpieceWeight: component.translations?.maxWorkpieceWeight,
        maxInjectionSpeed: component.translations?.maxInjectionSpeed,
      });

    return component;
  };
}
